import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import ClientForm from "../components/clients/ClientForm";
import {
  Users,
  Trophy,
  Activity,
  XOctagon,
  Search,
  Filter,
  Download,
  Plus,
  X,
  Mail,
  Phone,
  Building2,
  Globe,
  Briefcase,
  ExternalLink,
  KeyRound,
} from "lucide-react";
import AdminLayout from "../components/layout/AdminLayout";
import {
  useAdminStore,
  type AdminClientRow,
} from "../store/useAdminStore";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const initials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const formatDate = (iso: string) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const LEAD_STATUSES = [
  "Pending",
  "Need to Contact",
  "Contacted - No Response",
  "Contact in Future",
  "Negotiated",
  "Won Lead",
  "Lost Lead",
];

const LEAD_SOURCES = [
  "Upwork - Rizwan",
  "Upwork - Nadeem",
  "Website - LogoGuts",
  "Website - Multiversesol",
  "Website - e360Digital",
  "Other",
];

const statusStyle = (s: string) => {
  if (s === "Won Lead") return "bg-emerald-100 text-emerald-700 ring-emerald-200";
  if (s === "Lost Lead") return "bg-rose-100 text-rose-700 ring-rose-200";
  if (s === "Negotiated") return "bg-blue-100 text-blue-700 ring-blue-200";
  if (s === "Contact in Future") return "bg-violet-100 text-violet-700 ring-violet-200";
  if (s === "Need to Contact") return "bg-amber-100 text-amber-700 ring-amber-200";
  if (s === "Contacted - No Response") return "bg-orange-100 text-orange-700 ring-orange-200";
  return "bg-slate-100 text-slate-700 ring-slate-200";
};

const Kpi = ({
  label,
  value,
  sub,
  icon: Icon,
  tone = "blue",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tone?: "blue" | "emerald" | "rose" | "amber";
}) => {
  const tones = {
    blue: "from-blue-50 to-blue-100/40 text-blue-700",
    emerald: "from-emerald-50 to-emerald-100/40 text-emerald-700",
    rose: "from-rose-50 to-rose-100/40 text-rose-700",
    amber: "from-amber-50 to-amber-100/40 text-amber-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em]">
          {label}
        </span>
        <div
          className={`w-11 h-11 grid place-items-center rounded-xl bg-gradient-to-br ${tones[tone]}`}
        >
          <Icon size={20} />
        </div>
      </div>
      <p className="text-[32px] font-black leading-none tracking-tight text-slate-900">
        {value}
      </p>
      {sub && (
        <p className="text-[12px] text-slate-500 mt-2 font-medium">{sub}</p>
      )}
    </div>
  );
};

const AdminClientsPage = () => {
  const { adminClients, fetchAdminClients } = useAdminStore();
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [bdFilter, setBdFilter] = useState("");
  const [drawerClient, setDrawerClient] = useState<AdminClientRow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchAdminClients();
  }, [fetchAdminClients]);

  // Deep-link from a project drawer (?open=<clientId>) → open that client.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && adminClients?.clients) {
      const found = adminClients.clients.find((c) => c._id === openId);
      if (found) {
        setDrawerClient(found);
        searchParams.delete("open");
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminClients, searchParams]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const params: { bdId?: string; status?: string; source?: string; search?: string } = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (bdFilter) params.bdId = bdFilter;
      fetchAdminClients(params);
    }, 250);
    return () => clearTimeout(handler);
  }, [search, statusFilter, sourceFilter, bdFilter, fetchAdminClients]);

  const maxStatusCount = useMemo(
    () => Math.max(1, ...(adminClients?.byStatus.map((s) => s.count) || [0])),
    [adminClients]
  );
  const maxSourceCount = useMemo(
    () => Math.max(1, ...(adminClients?.bySource.map((s) => s.count) || [0])),
    [adminClients]
  );

  if (!adminClients) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 text-[13px]">
            Loading clients...
          </div>
        </div>
      </AdminLayout>
    );
  }

  const t = adminClients.totals;
  const conversionRate = t.total
    ? Math.round((t.won / t.total) * 100)
    : 0;

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
              All Contacts
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Every contact across all Business Developers · lead status & source
              tracking
            </p>
          </div>
          <div className="flex items-center gap-2">
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-[13px] font-semibold hover:brightness-110 transition"
          >
            <Plus size={15} />
            Add New Contact
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-[13px] text-slate-600 hover:bg-slate-50 transition">
            <Download size={15} />
            Export CSV
          </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi
            label="Total Contacts"
            value={t.total.toLocaleString()}
            sub={`${t.withLogin} with login access`}
            icon={Users}
            tone="blue"
          />
          <Kpi
            label="Active Leads"
            value={t.active.toLocaleString()}
            sub="In-progress conversations"
            icon={Activity}
            tone="amber"
          />
          <Kpi
            label="Won Leads"
            value={t.won.toLocaleString()}
            sub={`${conversionRate}% conversion rate`}
            icon={Trophy}
            tone="emerald"
          />
          <Kpi
            label="Lost Leads"
            value={t.lost.toLocaleString()}
            sub="Closed without conversion"
            icon={XOctagon}
            tone="rose"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Breakdown
            title="By Lead Status"
            items={adminClients.byStatus.map((s) => ({
              label: s.status,
              count: s.count,
              style: statusStyle(s.status),
            }))}
            max={maxStatusCount}
          />
          <Breakdown
            title="By Lead Source"
            items={adminClients.bySource.map((s) => ({
              label: s.source,
              count: s.count,
              style: "bg-slate-100 text-slate-700 ring-slate-200",
            }))}
            max={maxSourceCount}
          />
        </div>

        {adminClients.byBd.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-bold text-slate-900">
                Clients per Business Developer
              </h3>
              <span className="text-[11px] text-slate-500">
                Click a BD to filter
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {adminClients.byBd.map((bd) => {
                const active = bdFilter === bd.bdId;
                return (
                  <button
                    key={bd.bdId}
                    onClick={() =>
                      setBdFilter(bdFilter === bd.bdId ? "" : bd.bdId)
                    }
                    className={`text-left p-4 rounded-lg border transition ${
                      active
                        ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-200"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[10px] font-bold">
                        {initials(bd.name)}
                      </div>
                      <span className="text-[13px] font-semibold text-slate-900 truncate">
                        {bd.name}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[22px] font-extrabold text-slate-900">
                        {bd.total}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        clients
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-[10px]">
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold">
                        {bd.won} won
                      </span>
                      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-bold">
                        {bd.active} active
                      </span>
                      <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 font-bold">
                        {bd.lost} lost
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3">
            <div className="flex-1 relative min-w-[260px]">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or company..."
                className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
            >
              <option value="">Status: All</option>
              {LEAD_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
            >
              <option value="">Source: All</option>
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {(search || statusFilter || sourceFilter || bdFilter) && (
              <button
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setSourceFilter("");
                  setBdFilter("");
                }}
                className="text-[12px] text-blue-600 hover:text-blue-800 font-semibold"
              >
                Clear filters
              </button>
            )}
            <button className="p-2.5 text-slate-500 hover:bg-slate-100 rounded-lg border border-slate-200">
              <Filter size={16} />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <Th>Contact</Th>
                  <Th>Company</Th>
                  <Th>Owned By (BD)</Th>
                  <Th align="center">Lead Status</Th>
                  <Th>Source</Th>
                  <Th align="center">Projects</Th>
                  <Th align="right">Revenue</Th>
                  <Th>Created</Th>
                  <Th align="center">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {adminClients.clients.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-6 py-14 text-center text-slate-400 text-[13px]"
                    >
                      No clients match the current filters.
                    </td>
                  </tr>
                ) : (
                  adminClients.clients.map((c) => (
                    <tr
                      key={c._id}
                      className="hover:bg-slate-50/50 transition cursor-pointer"
                      onClick={() => setDrawerClient(c)}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[11px] font-bold shadow-sm">
                            {initials(c.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] font-bold text-slate-900 flex items-center gap-1.5">
                              {c.name}
                              {c.hasLogin && (
                                <KeyRound
                                  size={11}
                                  className="text-emerald-600"
                                />
                              )}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate max-w-[200px]">
                              {c.email || c.username}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-[13px] text-slate-700">
                          {c.company || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        {c.bd ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-slate-200 grid place-items-center text-[9px] font-bold text-slate-600">
                              {initials(c.bd.name)}
                            </div>
                            <span className="text-[12px] text-slate-700 font-medium">
                              {c.bd.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-[12px]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${statusStyle(
                            c.leadStatus
                          )}`}
                        >
                          {c.leadStatus}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-[11px] text-slate-600 px-2 py-0.5 bg-slate-100 rounded">
                          {c.leadSource}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-[13px] font-bold text-slate-900">
                            {c.projects.total}
                          </span>
                          <span className="text-[9px] text-slate-500">
                            {c.projects.active}a · {c.projects.completed}c
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-[13px] font-extrabold text-[#006a61] font-mono">
                          {fmtMoney(c.projects.revenue)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-[12px] text-slate-500">
                          {formatDate(c.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDrawerClient(c);
                          }}
                          className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1"
                        >
                          View
                          <ExternalLink size={11} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {adminClients.clients.length > 0 && (
            <div className="bg-slate-50/50 border-t border-slate-200 px-5 py-3 text-[12px] text-slate-500">
              Showing {adminClients.clients.length} client
              {adminClients.clients.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>

      {drawerClient && (
        <ClientDrawer
          client={drawerClient}
          onClose={() => setDrawerClient(null)}
        />
      )}

      {/* Add a contact. The same form the BD contacts page uses, so a contact
          created here is identical to one created there — there is no separate
          "admin contact" to go wrong later. */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          addOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b bg-slate-50">
          <h2 className="text-xl font-semibold text-slate-800">Add New Contact</h2>
          <button
            onClick={() => setAddOpen(false)}
            className="p-1.5 rounded-full hover:bg-slate-200 transition"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto h-[calc(100%-5rem)] p-6">
          <ClientForm
            onSuccess={() => {
              setAddOpen(false);
              fetchAdminClients();
            }}
            onCredentialsReady={(name, email, password) => {
              navigator.clipboard
                .writeText(`Name: ${name}\nEmail: ${email}\nPassword: ${password}`)
                .then(() => toast.success("Credentials copied!"));
              setAddOpen(false);
              fetchAdminClients();
            }}
          />
        </div>
      </div>

      {addOpen && (
        <div onClick={() => setAddOpen(false)} className="fixed inset-0 bg-black/30 z-40" />
      )}
    </AdminLayout>
  );
};

const Breakdown = ({
  title,
  items,
  max,
}: {
  title: string;
  items: { label: string; count: number; style: string }[];
  max: number;
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-5">
    <h3 className="text-[15px] font-bold text-slate-900 mb-4">{title}</h3>
    <div className="space-y-3">
      {items.map((it) => (
        <div key={it.label}>
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ${it.style}`}
            >
              {it.label}
            </span>
            <span className="text-[13px] font-extrabold text-slate-900">
              {it.count}
            </span>
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
              style={{ width: `${(it.count / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ClientDrawer = ({
  client,
  onClose,
}: {
  client: AdminClientRow;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  return (
  <div
    className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm"
    onClick={onClose}
  >
    <aside
      onClick={(e) => e.stopPropagation()}
      className="absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
    >

      <div className="p-6 border-b border-slate-200 bg-gradient-to-br from-blue-50/60 to-white">
        <div className="flex items-start justify-between mb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-xl font-black shadow">
            {initials(client.name)}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <h2 className="text-[20px] font-extrabold text-slate-900">
          {client.name}
        </h2>
        <p className="text-[12px] text-slate-500 mt-0.5">@{client.username}</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span
            className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${statusStyle(
              client.leadStatus
            )}`}
          >
            {client.leadStatus}
          </span>
          <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${client.contactStatus === "Client" ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-100 text-slate-600 ring-slate-200"}`}>
            {client.contactStatus || "Lead"}
          </span>
          <span className="text-[11px] text-slate-600 px-2 py-0.5 bg-slate-100 rounded">
            {client.leadSource}
          </span>
          {client.hasLogin && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 px-2 py-0.5 bg-emerald-50 rounded font-semibold">
              <KeyRound size={11} />
              Has Login
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">

        <div>
          <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
            Contact
          </h3>
          <div className="space-y-2.5">
            <InfoRow icon={Mail} value={client.email || "—"} />
            <InfoRow icon={Phone} value={client.phone || "—"} />
            <InfoRow icon={Building2} value={client.company || "—"} />
            <InfoRow icon={Globe} value={client.website || "—"} />
          </div>
        </div>

        <div>
          <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
            Business Developer
          </h3>
          {client.bd ? (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[11px] font-bold">
                {initials(client.bd.name)}
              </div>
              <div>
                <p className="text-[13px] font-bold text-slate-900">
                  {client.bd.name}
                </p>
                <p className="text-[11px] text-slate-500">{client.bd.email}</p>
              </div>
            </div>
          ) : (
            <p className="text-[12px] text-slate-400">Unassigned</p>
          )}
        </div>

        <div>
          <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
            Project Activity
          </h3>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Total" value={client.projects.total} />
            <Stat label="Active" value={client.projects.active} tone="amber" />
            <Stat
              label="Completed"
              value={client.projects.completed}
              tone="emerald"
            />
          </div>
          <div className="mt-3 p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
              Revenue Generated
            </p>
            <p className="text-[22px] font-black text-emerald-800 mt-1">
              {fmtMoney(client.projects.revenue)}
            </p>
          </div>
        </div>

        {client.notes && (
          <div>
            <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">
              Notes
            </h3>
            <p className="text-[12px] text-slate-700 leading-relaxed bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">
              {client.notes}
            </p>
          </div>
        )}

        <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-500">
          Added on {formatDate(client.createdAt)}
        </div>
      </div>

      <div className="p-4 border-t border-slate-200 bg-slate-50/50">
        <button
          disabled={client.projects.total === 0}
          onClick={() =>
            navigate(`/admin/projects?client=${client._id}&name=${encodeURIComponent(client.name)}`)
          }
          className="w-full px-3 py-2 text-[12px] font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Briefcase size={14} />
          {client.projects.total === 0 ? "No projects yet" : `View ${client.projects.total} Project${client.projects.total === 1 ? "" : "s"}`}
        </button>
      </div>
    </aside>
  </div>
  );
};

const InfoRow = ({ icon: Icon, value }: { icon: any; value: string }) => (
  <div className="flex items-center gap-2.5 text-[12px] text-slate-700">
    <Icon size={14} className="text-slate-400 shrink-0" />
    <span className="truncate">{value}</span>
  </div>
);

const Stat = ({
  label,
  value,
  tone = "blue",
}: {
  label: string;
  value: number;
  tone?: "blue" | "amber" | "emerald";
}) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className={`p-2.5 rounded-lg text-center ${tones[tone]}`}>
      <p className="text-[18px] font-extrabold leading-none">{value}</p>
      <p className="text-[9px] uppercase tracking-wider mt-1 font-bold">
        {label}
      </p>
    </div>
  );
};

const Th = ({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}) => (
  <th
    className={`px-4 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-${align}`}
  >
    {children}
  </th>
);

export default AdminClientsPage;
